import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarContext } from '@/contexts/CalendarContext';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { getWeekDates } from '@/components/utils/DateUtils';
import { dateLocales } from '@/i18n/dateLocales';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';
import WeekView, { WeekViewFlags } from '@/components/calendar/weekly/WeekView';
import WeekSelect from '@/components/calendar/weekly/WeekSelect';
import { themeToWeekTokens } from '@/components/calendar/weekly/weekTokens';

/**
 * WeekPage (adult) — tunn wrapper runt den gemensamma WeekView. Vuxenvyn är ren
 * och lugn: ingen maskot/gamification, dagar alltid öppna. Adult BEHÅLLER sin
 * veckobläddring — WeekSelect renderas i navigerings-slotten och de 7 dagskorten
 * följer CalendarContext.selectedWeek.
 *
 * Detta ersätter den tidigare tidslinje-vyn (WeekCalendar) med den gemensamma
 * dagskorts-strukturen (den enda stora synliga ändringen, godkänd av Blomman).
 * Veckans completion syns via klar-räknaren {dayDone}/{dayTotal} i varje dagskort
 * (showDayCount) — ersätter den tidigare separata CompletedWeeklyActivities-widgeten.
 */
/**
 * Namn som redan slutar på s-ljud får ingen extra genitiv-s: "Lukas vecka", inte
 * "Lukass vecka" (och "Lukas' week" på engelska). Regeln gäller de språk som bildar
 * genitiv med s; för övriga pekar båda i18n-nycklarna på samma formulering, så
 * villkoret blir en tom växel i stället för ett fel.
 */
const endsWithSibilant = (name: string): boolean => /[sxzSXZ]$/.test(name.trim());

const ADULT_FLAGS: WeekViewFlags = {
  collapsibleDays: false,
  showDayCount: true,
  showWeekProgress: false,
  showDayStar: false,
  showStatusDots: false,
  showActivityEmoji: false,
  showCompanion: false,
  todayGlow: false,
};

const WeekPage = () => {
  const { t, i18n } = useTranslation();
  const { selectedWeek } = useContext(CalendarContext);
  const { currentClient, isSupervising } = useContext(SupervisorContext);
  const { selectedTheme, isDarkMode } = useThemePreference();

  // Handledaren tittar på NÅGON ANNANS vecka. "Din vecka" i rubriken fick vyn att se
  // ut som handledarens egen, trots att varje rad är brukarens — därför står
  // brukarens namn i rubriken så fort en brukare är vald. Brukaren själv ser som
  // förut "Din vecka"; det är fortfarande hens.
  const clientName = isSupervising ? currentClient?.name ?? currentClient?.tag : undefined;
  const title = clientName
    ? t(endsWithSibilant(clientName) ? 'week.clientTitleSibilant' : 'week.clientTitle', {
        name: clientName,
      })
    : t('week.title');

  const locale = dateLocales[i18n.language.split('-')[0]] ?? dateLocales.en;
  const tokens = themeToWeekTokens(getThemeTokens(selectedTheme, isDarkMode), isDarkMode);
  const weekStart = getWeekDates(selectedWeek).startDate;

  return (
    <WeekView
      title={title}
      weekStart={weekStart}
      tokens={tokens}
      flags={ADULT_FLAGS}
      weekNav={<WeekSelect />}
      todayLabel={t('week.today')}
      noActivitiesLabel={t('week.noActivities')}
      weekProgressLabel=""
      nowLabel={t('week.now')}
      locale={locale}
    />
  );
};

export default WeekPage;
